import {
  createContext,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';

/**
 * Chrome state whose lifetime is the HOST APP rather than a terminal mount.
 *
 * The hosts close the terminal by UNMOUNTING it — Paradex's drawer, Paradigm's
 * dock — so anything held in `TerminalLayout` dies with the surface. The
 * sidebar collapse did: every reopen came back expanded however the user had
 * left it.
 *
 * This is the same shape as the auth/connection cache `DimeTerminalProvider`
 * already holds (see `sessionCache.ts`): created once, above every surface, so
 * mounts below it come and go while the value survives. Memory-only, and
 * deliberately so — this is per-session UI state, not a preference. It must
 * NOT reach localStorage or the sync doc: a collapsed sidebar is a statement
 * about what the user is doing right now, and restoring it in a new tab next
 * week would be a different claim than the one they made. Anything that should
 * outlive the page belongs in `PreferencesProvider` instead.
 *
 * OPTIONAL by design. `<Terminal>` is mountable standalone (the app does it,
 * and `DimeCloudTerminal` supports it), so the reader returns null rather than
 * throwing and the layout falls back to per-mount state — the old behaviour,
 * which is the right answer when there is no surrounding app to outlive.
 */
export interface ShellState {
  /**
   * Whether the sessions sidebar is expanded. `null` ⇒ nothing has decided
   * yet, so the layout derives it from the viewport on first mount; a concrete
   * value means a user (or a breakpoint crossing) set it and it stands.
   */
  readonly sidebarOpen: boolean | null;
  readonly setSidebarOpen: Dispatch<SetStateAction<boolean | null>>;
}

const ShellStateCtx = createContext<ShellState | null>(null);

/**
 * The surrounding app's shell state, or null when the terminal is mounted
 * without a `<ShellStateProvider>` above it. Non-throwing: absence is a
 * supported configuration, not a mistake.
 */
export function useOptionalShellState(): ShellState | null {
  return useContext(ShellStateCtx);
}

/**
 * Mount ONCE, above every terminal surface — `DimeTerminalProvider` already
 * does this for cloud hosts, so they need no change. Hosts that mount
 * `<Terminal>` directly can wrap it themselves to get the same continuity.
 */
export function ShellStateProvider({
  children,
}: {
  readonly children: ReactNode;
}): JSX.Element {
  const [sidebarOpen, setSidebarOpen] = useState<boolean | null>(null);
  const value = useMemo<ShellState>(
    () => ({ sidebarOpen, setSidebarOpen }),
    [sidebarOpen],
  );
  return (
    <ShellStateCtx.Provider value={value}>{children}</ShellStateCtx.Provider>
  );
}
